import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromStringOrNull } from '../../serialization/enum-utils.js';
import { kMaxServiceQuantity } from '../../utility/constants.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Cardinal } from '../core/cardinal.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { BusinessPrivacyMode } from './business-privacy-mode.js';
import { ClusteredSchedulingSettings } from './clustered-scheduling-settings.js';
import { SchedulePostProcessorType } from './schedule-post-processor-type.js';

export class CalendarBookingSettings extends DataModel {
  static readonly TYPE_ID: string = '4cbc0e9786e54d3e914658d0edc81dc1';
  static readonly SCHEMA_VERSION: number = 8;

  constructor(
    /**
   * Defines the business visibility and booking settings:
   *    [BusinessPrivacyMode.Public] means the business is visible and its
   *    services are bookable by everyone.
   *
   *    [BusinessPrivacyMode.Hybrid] means the business is visible by everyone,
   *    but its services are bookable by invited clients only.
   *
   *    [BusinessPrivacyMode.Hidden] means the business is visible and its
   *    services are bookable by invited clients only.
   *
   * @deprecated Please use BusinessPrivacySettings instead. Remove after migrating all businesses to version 15.
   */
    public readonly legacyPrivacyMode: BusinessPrivacyMode | null,

    // If [true] the client will be able to open the calendar view and select a specific day.
    public readonly allowCalendarView: boolean = true,

    // Clients shouldn't be able to see booking times that are before [NOW + bookingInAdvance].
    public readonly bookingInAdvance: Duration = dayjs.duration(30, 'minutes'),

    // Clients shouldn't be able to see booking times that are after [NOW + bookingForFuture].
    public readonly bookingForFuture: Duration = dayjs.duration(30, 'days'),

    // Clients shouldn't be able to cancel appointments that will start before [NOW + cancellation].
    public readonly cancellation: Duration = dayjs.duration(24, 'hours'),

    // The interval shown on the client app between appointment suggested times.
    public readonly smartSchedule: Duration = dayjs.duration(30, 'minutes'),

    // Clients shouldn't be able to book in the interval
    // [startOfAppointment - breakBetweenAppointments, endOfAppointment + breakBetweenAppointments].
    public readonly breakBetweenAppointments: Duration = dayjs.duration(0, 'minutes'),

    // What post processor to use to optimize the booking times on the client app.
    public readonly schedulePostProcessor: SchedulePostProcessorType = SchedulePostProcessorType.Basic,

    public readonly clusteredSchedulingSettings: ClusteredSchedulingSettings,

    // Number of unique services that can be booked in a single appointment.
    public readonly maxUniqueServices: Cardinal,

    // Number of instances of the same service that can be booked in a single appointment.
    public readonly maxInstancesPerService: Cardinal,
  ) {
    super();
  }

  get $typeId(): string {
    return CalendarBookingSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(CalendarBookingSettings.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): CalendarBookingSettings {
    return new CalendarBookingSettings(
        toEnumFromStringOrNull(MapUtils.getOrNull<string>(map, 'privacyMode'), BusinessPrivacyMode),
        MapUtils.get<boolean>(map, 'allowCalendarView'),
        context.decodeDuration(MapUtils.get<number>(map, 'bookingInAdvance')),
        context.decodeDuration(MapUtils.get<number>(map, 'bookingForFuture')),
        context.decodeDuration(MapUtils.get<number>(map, 'cancellation')),
        context.decodeDuration(MapUtils.get<number>(map, 'smartSchedule')),
        context.decodeDuration(MapUtils.get<number>(map, 'breakBetweenAppointments')),
        MapUtils.get<SchedulePostProcessorType>(map, 'schedulePostProcessor'),
        context.fromData<ClusteredSchedulingSettings>(
          MapUtils.getRaw(map, 'clusteredSchedulingSettings') as SerializedData,
        ),
        Cardinal.decode(MapUtils.get<number>(map, 'maxUniqueServices')),
        Cardinal.decode(MapUtils.get<number>(map, 'maxInstancesPerService')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'privacyMode': fromEnumAsStringOrNull(this.legacyPrivacyMode),
      'allowCalendarView': this.allowCalendarView,
      'bookingInAdvance': context.encodeDuration(this.bookingInAdvance),
      'bookingForFuture': context.encodeDuration(this.bookingForFuture),
      'cancellation': context.encodeDuration(this.cancellation),
      'smartSchedule': context.encodeDuration(this.smartSchedule),
      'breakBetweenAppointments': context.encodeDuration(this.breakBetweenAppointments),
      'schedulePostProcessor': this.schedulePostProcessor,
      'clusteredSchedulingSettings': context.toData(this.clusteredSchedulingSettings),
      'maxUniqueServices': this.maxUniqueServices.encode(),
      'maxInstancesPerService': this.maxInstancesPerService.encode(),
    };
  }

  clone(): this {
    return new CalendarBookingSettings(
        this.legacyPrivacyMode,
        this.allowCalendarView,
        this.bookingInAdvance,
        this.bookingForFuture,
        this.cancellation,
        this.smartSchedule,
        this.breakBetweenAppointments,
        this.schedulePostProcessor,
        this.clusteredSchedulingSettings.clone(),
        this.maxUniqueServices.clone(),
        this.maxInstancesPerService.clone(),
    ) as this;
  }

  isSame(other: CalendarBookingSettings): boolean {
    if(this === other) {
      return true;
    }

    return other.allowCalendarView === this.allowCalendarView &&
            other.bookingInAdvance === this.bookingInAdvance &&
            other.bookingForFuture === this.bookingForFuture &&
            other.cancellation === this.cancellation &&
            other.smartSchedule === this.smartSchedule &&
            other.breakBetweenAppointments === this.breakBetweenAppointments &&
            other.schedulePostProcessor === this.schedulePostProcessor &&
            isSameData(other.clusteredSchedulingSettings, this.clusteredSchedulingSettings) &&
            isSameData(other.maxUniqueServices, this.maxUniqueServices) &&
            isSameData(other.maxInstancesPerService, this.maxInstancesPerService);
  }
}

export class CalendarBookingSettingsBuilder extends DataModelBuilder<CalendarBookingSettings> {
  build(context: SerializationContext, map: SerializedData): CalendarBookingSettings {
    return CalendarBookingSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(CalendarBookingSettings.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(CalendarBookingSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;
    if(version < 2) {
      // I think these fields where added without creating migrations so old businesses will fail to deserialize.
      // We didn't notice it until now because the apps don't have null safety on fromData and the
      // constructor assigns default values if you pass `null`.
      MapUtils.set(map, 'clusteredSchedulingSettings', context.toData(new ClusteredSchedulingSettings()));
      MapUtils.set(map, 'schedulePostProcessor', fromEnumAsString(SchedulePostProcessorType.Clustered));
      MapUtils.set(map, 'allowCalendarView', true);
      MapUtils.set(map, 'scheduleInterval', dayjs.duration(30, 'minutes').asMilliseconds());

      MapUtils.set(map, 'bookableServiceCount', Cardinal.infinity.encode());
      version = 2;
    }

    if(version < 3) {
      const allowPublicListing = MapUtils.get(map, 'allowPublicListing');
      const allowPublicCalendar = MapUtils.get(map, 'allowPublicCalendar');

      if(allowPublicListing) {
        if(allowPublicCalendar) {
          MapUtils.set(map, 'privacyMode', BusinessPrivacyMode.Public);
        }
        else {
          MapUtils.set(map, 'privacyMode', BusinessPrivacyMode.Hybrid);
        }
      }
      else {
        MapUtils.set(map, 'privacyMode', BusinessPrivacyMode.Hidden);
      }

      version = 3;
    }

    if(version < 4) {
      MapUtils.set(map, 'smartSchedule', MapUtils.get(map, 'scheduleInterval'));

      version = 4;
    }

    // In this version `breakBetweenAppointments` of type [Duration] was introduced.
    if(version < 5) {
      MapUtils.set(map, 'breakBetweenAppointments', 0);

      version = 5;
    }

    // In this version the `Internal` privacy mode was introduced.
    if(version < 6) {
      MapUtils.set(
          map, 'privacyMode',
          MapUtils.getOrNull(map, 'v6_privacyMode') ?? MapUtils.getOrNull(map, 'privacyMode')
      );

      version = 6;
    }

    // In this version the [privacyMode] field was removed.
    if(version < 7) {
      version = 7;
    }

    // In this version the [maxInstancesPerService] field was added
    // and [bookableServiceCount] was renamed to [maxUniqueServices].
    if(version < 8) {
      MapUtils.set(map, 'maxUniqueServices', MapUtils.get(map, 'bookableServiceCount'));
      MapUtils.setIfNullOrUndefined(
          map,
          'maxInstancesPerService',
          Cardinal.finite(kMaxServiceQuantity).encode(),
      );

      version = 8;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
